<script setup lang="tsx">
import {
  StyleValue,
  onMounted,
  onUnmounted,
  ref,
  watch,
  watchEffect
} from "vue";
import { throttle } from "@zyzgroup/core-common";
import {
  getInnerText,
  useClipboard,
  tap$,
  DrawShape,
  DrawType,
  DrawContext,
  requestAnimationFrameCycle
} from "@zyzgroup/core-web";

// for merge ce shadow-root styles
defineOptions({
  customOptions: {},
  inheritAttrs: true
});

export type DisplayType = "full" | "shrink" | "empty";

const props = withDefaults(
  defineProps<{
    type?: DrawType;
    display?: DisplayType;
    shapes?: DrawShape[]; // const shapes = ref([]);
    loop?: boolean;
    enableCustomContext?: boolean;
    supportCopyToClipboard?: boolean; // 是否支持复制文字
  }>(),
  {
    type: "div",
    display: "full",
    shapes: () => [],
    loop: false,
    enableCustomContext: false,
    supportCopyToClipboard: false
  }
);

const emits = defineEmits<{
  (event: "mount", box: { el: HTMLElement; rect: DOMRect }): void;
  (event: "resize", box: { el: HTMLElement; rect: DOMRect }): void;
  (
    event: "start",
    drawContext: DrawContext,
    box: { el: HTMLElement; rect: DOMRect }
  ): void;
  (
    event: "frame",
    drawContext: DrawContext,
    box: { el: HTMLElement; rect: DOMRect }
  ): void;
  (
    event: "end",
    drawContext: DrawContext,
    box: { el: HTMLElement; rect: DOMRect }
  ): void;
  (event: "copyToClipboardSuccess", text: string): void;
  (event: "copyToClipboardError", err: string): void;
  (event: "throttledClick", e: Event): void;
  (event: "click", e: Event): void;
}>();

const r = ref();

let drawContext;
let resizeSubscription;
let tapSubscription;
let cancelAnimationFrame;

let isEmitMount = false;

// props.shapes 地址更新时，重新 emits("start")
let isShapesUpdated = true;
watch(
  () => props.shapes,
  (newVal, oldVal) => {
    if (!drawContext) {
      return;
    }
    if (!r.value) {
      return;
    }
    isShapesUpdated = newVal !== oldVal;
    if (isShapesUpdated) {
      startAnimationFrame(drawContext, {
        el: r.value,
        rect: drawContext.rect
      });
    }
  }
);

function startAnimationFrame(drawContext, box) {
  cancelAnimationFrame?.();
  emits("start", drawContext, box);
  cancelAnimationFrame = requestAnimationFrameCycle(() => {
    emits("frame", drawContext, box);
    if (props.shapes.length) {
      if (isShapesUpdated) {
        drawContext.draw(...props.shapes);
        isShapesUpdated = false;
      } else {
        drawContext.draw(); // 传入空数组：保持原 shapes 以提高性能
      }
    } else {
      drawContext.clear();
      cancelAnimationFrame?.();
      emits("end", drawContext, box);
    }
    return props.loop;
  });
}

watchEffect(() => {
  if (!r.value) {
    // not mounted yet, or the element was unmounted (e.g. by v-if)
    return;
  }

  if (!drawContext) {
    drawContext = new DrawContext(
      r.value,
      props.type,
      props.enableCustomContext
    );
    resizeSubscription = drawContext.size$.subscribe((rect) => {
      if (rect) {
        const box = { el: r.value, rect };
        if (!isEmitMount) {
          isEmitMount = true;
          emits("mount", box);
        }
        emits("resize", box);
        startAnimationFrame(drawContext, box);
      }
    });
  }

  if (props.supportCopyToClipboard && !tapSubscription) {
    tapSubscription = tap$(r.value).subscribe(() => {
      const text = getInnerText(r.value);
      useClipboard(text)
        .then(() => {
          emits("copyToClipboardSuccess", text);
        })
        .catch((err) => {
          emits("copyToClipboardError", err.toString());
        });
    });
  }
});

const isClicked = ref(false);
const throttledClick = throttle((e: Event) => {
  isClicked.value = true;
  setTimeout(() => {
    isClicked.value = false;
    emits("throttledClick", e);
    emits("click", e);
    // const dataset = (e.target as HTMLElement).dataset;
  }, 100);
  // const dataset = (e.currentTarget as HTMLElement).dataset;

  // 注意 ce 中不可用： const dataset = ((e.currentTarget || e.target) as HTMLElement).dataset;
  // ce 中 e.currentTarget 为 null, e.target 为 customElement 实例
}, 300);

onMounted(() => {});

onUnmounted(() => {
  cancelAnimationFrame?.();
  resizeSubscription?.unsubscribe();
  tapSubscription?.unsubscribe();
  drawContext?.destroy();
});

// 不推荐使用
defineExpose({
  getElement: () => r.value,
  getRect: () => r.value.getBoundingClientRect()
});
</script>

<template>
  <!-- v-bind="$attrs" -->
  <div
    ref="r"
    class="box"
    :style="
      props.display == 'empty' ? {} :
      Object.assign(
        props.display == 'shrink' ? {
          display:'inline-block',
          width: 'auto',
          height: 'auto'
        } :
        props.display == 'full' ? {
          display:  'block',
          width:  '100%',
          height: '100%',
        } :
        {},
        {
          position: 'relative',
          boxSizing: 'border-box',
          userSelect: 'auto',
          /* overflow: 'hidden', */
          /* pointer-events: 'none', */
          /* cursor: 'pointer' */
        }
      ) as StyleValue
    "
    @click="throttledClick"
  >
    <slot></slot>
  </div>
</template>
